// =========================================
// LandingCMS.jsx
// ORION BEACH HOTEL · KITE HOUSE
// CMS PREMIUM · HOTEL BOUTIQUE
// DESARROLLO + PRODUCCIÓN
// =========================================

import { useEffect, useState } from "react";
import "../styles/cmsLanding.css";

// =========================================
// API CONFIGURATION
// =========================================
//
// DESARROLLO
// VITE_API_URL=http://127.0.0.1:8000
//
// PRODUCCIÓN
// VITE_API_URL=https://hotelorionmayapo.com
//
// IMPORTANTE:
// En producción también podríamos usar simplemente:
//
// VITE_API_URL=""
//
// porque Nginx manejará:
//
// https://hotelorionmayapo.com/api/
//
// =========================================

const API = (
  import.meta.env.VITE_API_URL ||
  (
    import.meta.env.DEV
      ? "http://127.0.0.1:8000/api"
      : "https://hotelorionmayapo.com/api"
  )
).replace(/\/+$/, "");

// =========================================
// API URL HELPER
// =========================================
//
// Permite construir correctamente:
//
// /api/content/hero
// /api/content/results
// /api/upload
//
// sin terminar con:
//
// //api
//
// =========================================

const apiUrl = (path = "") => {

  const normalizedPath =
    path.startsWith("/")
      ? path
      : `/${path}`;

  return `${API}${normalizedPath}`;

};

// =========================================
// DEFAULT HERO
// =========================================

const DEFAULT_HERO = {

  content: {

    badge:
      "HOTEL ORIÓN • GUAJIRA",

    title:
      "Descanso frente al paraíso",

    subtitle:
      "Vive el viento. Descansa en Orión.",

    description:
      "Disfruta una experiencia única frente al mar en La Guajira. Hospedaje, naturaleza, tranquilidad y la energía del kitesurf en un solo lugar.",

    phone:
      "573001234567",

    whatsapp_text:
      "Hola, quiero información sobre disponibilidad y reservas en Orión Beach Hotel.",

    primary_button:
      "Reservar Ahora",

    secondary_button:
      "Ver Habitaciones"

  },

  images: []

};

// =========================================
// DEFAULT CONTENT
// =========================================

const DEFAULT_RESULTS = [];

// =========================================
// SAFE JSON
// =========================================
//
// Evita errores cuando Django devuelve:
//
// - JSON
// - HTML
// - respuesta vacía
// - error 500
//
// =========================================

const readJson = async (response) => {

  try {

    return await response.json();

  } catch {

    return {};

  }

};

// =========================================
// API ERROR
// =========================================

const getApiError = (
  data,
  response,
  fallback
) => {

  return (
    data?.error ||
    data?.message ||
    data?.detail ||
    fallback ||
    `Error HTTP ${response.status}`
  );

};

// =========================================
// UPLOAD IMAGE
// =========================================

const uploadImage = async (file) => {

  if (!file) {

    throw new Error(
      "No se seleccionó ninguna imagen."
    );

  }

  const formData =
    new FormData();

  formData.append(
    "file",
    file
  );

  const response =
    await fetch(
      apiUrl("/api/upload"),
      {
        method: "POST",
        body: formData,
        headers: {
          Accept:
            "application/json"
        }
      }
    );

  const data =
    await readJson(
      response
    );

  if (!response.ok) {

    throw new Error(
      getApiError(
        data,
        response,
        "Error subiendo imagen."
      )
    );

  }

  if (!data?.url) {

    throw new Error(
      "El servidor no devolvió la URL de la imagen."
    );

  }

  return data.url;

};

// =========================================
// NORMALIZE HERO
// =========================================

const normalizeHero = (
  data
) => {

  if (
    !data ||
    typeof data !== "object"
  ) {

    return {

      ...DEFAULT_HERO,

      content: {
        ...DEFAULT_HERO.content
      },

      images: []

    };

  }

  return {

    ...DEFAULT_HERO,

    ...data,

    content: {

      ...DEFAULT_HERO.content,

      ...(data.content || {})

    },

    images:

      Array.isArray(
        data.images
      )

        ? data.images

        : []

  };

};

// =========================================
// NORMALIZE CONTENT
// =========================================

const normalizeResults = (
  data
) => {

  if (
    !data ||
    typeof data !== "object"
  ) {

    return [];

  }

  if (
    data.content &&
    Array.isArray(
      data.content.items
    )
  ) {

    return data.content.items;

  }

  if (
    Array.isArray(
      data.items
    )
  ) {

    return data.items;

  }

  return [];

};

// =========================================
// COMPONENT
// =========================================

export default function LandingCMS() {

  // =========================================
  // STATES
  // =========================================

  const [hero, setHero] =
    useState(
      DEFAULT_HERO
    );

  const [results, setResults] =
    useState(
      DEFAULT_RESULTS
    );

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [savingHero, setSavingHero] =
    useState(false);

  const [savingResults, setSavingResults] =
    useState(false);

  const [uploadingHero, setUploadingHero] =
    useState(false);

  const [uploadingResult, setUploadingResult] =
    useState(null);

  // =========================================
  // ENVIRONMENT
  // =========================================

  const isProduction =
    import.meta.env.PROD;

  // =========================================
  // LOAD
  // =========================================

  useEffect(() => {

    loadAll();

  }, []);

  // =========================================
  // LOAD ALL
  // =========================================

  const loadAll = async () => {

    setLoading(true);

    setError("");

    try {

      // =====================================
      // HERO
      // =====================================

      const heroRes =
        await fetch(
          apiUrl(
            "/api/content/hero"
          ),
          {
            method: "GET",

            headers: {
              Accept:
                "application/json"
            }
          }
        );

      const heroData =
        await readJson(
          heroRes
        );

      if (!heroRes.ok) {

        throw new Error(
          getApiError(
            heroData,
            heroRes,
            `Error cargando Hero: HTTP ${heroRes.status}`
          )
        );

      }

      setHero(
        normalizeHero(
          heroData
        )
      );

      // =====================================
      // CONTENT / EXPERIENCES
      // =====================================

      try {

        const resultsRes =
          await fetch(
            apiUrl(
              "/api/content/results"
            ),
            {
              method: "GET",

              headers: {
                Accept:
                  "application/json"
              }
            }
          );

        if (
          resultsRes.ok
        ) {

          const resultsData =
            await readJson(
              resultsRes
            );

          setResults(
            normalizeResults(
              resultsData
            )
          );

        } else {

          console.warn(
            "No se pudo cargar el contenido del hotel.",
            resultsRes.status
          );

          setResults([]);

        }

      } catch (
        resultsError
      ) {

        console.warn(
          "CONTENT ERROR:",
          resultsError
        );

        setResults([]);

      }

    } catch (err) {

      console.error(
        "CMS ERROR:",
        err
      );

      setError(
        err.message ||
        "No fue posible cargar el CMS."
      );

      setHero(
        normalizeHero(
          DEFAULT_HERO
        )
      );

      setResults([]);

    } finally {

      setLoading(false);

    }

  };

  // =========================================
  // UPDATE HERO
  // =========================================

  const updateHeroField = (
    field,
    value
  ) => {

    setHero(
      (prev) => {

        const safeHero =
          prev ||
          DEFAULT_HERO;

        return {

          ...safeHero,

          content: {

            ...(safeHero.content || {}),

            [field]:
              value

          }

        };

      }
    );

  };

  // =========================================
  // HERO IMAGE
  // =========================================

  const handleHeroImage = async (
    file
  ) => {

    if (!file) {

      return;

    }

    try {

      setUploadingHero(
        true
      );

      const imageUrl =
        await uploadImage(
          file
        );

      setHero(
        (prev) => {

          const safeHero =
            prev ||
            DEFAULT_HERO;

          return {

            ...safeHero,

            images: [

              {
                type:
                  "hero",

                url:
                  imageUrl

              }

            ]

          };

        }
      );

    } catch (err) {

      console.error(
        "HERO IMAGE ERROR:",
        err
      );

      alert(
        err.message ||
        "Error subiendo imagen."
      );

    } finally {

      setUploadingHero(
        false
      );

    }

  };

  // =========================================
  // SAVE HERO
  // =========================================

  const saveHero = async () => {

    try {

      setSavingHero(
        true
      );

      setError("");

      const safeHero =
        hero ||
        DEFAULT_HERO;

      const response =
        await fetch(
          apiUrl(
            "/api/content/hero"
          ),
          {

            method:
              "PUT",

            headers: {

              "Content-Type":
                "application/json",

              Accept:
                "application/json"

            },

            body:
              JSON.stringify({

                content:
                  safeHero.content ||
                  {},

                images:
                  Array.isArray(
                    safeHero.images
                  )
                    ? safeHero.images
                    : []

              })

          }
        );

      const data =
        await readJson(
          response
        );

      if (!response.ok) {

        throw new Error(
          getApiError(
            data,
            response,
            `Error guardando Hero: HTTP ${response.status}`
          )
        );

      }

      if (
        data &&
        typeof data === "object"
      ) {

        setHero(
          normalizeHero(
            data
          )
        );

      }

      alert(
        "✓ Experiencia principal actualizada correctamente."
      );

    } catch (err) {

      console.error(
        "SAVE HERO ERROR:",
        err
      );

      alert(

        `✕ ${
          err.message ||
          "Error guardando la experiencia principal."
        }`

      );

    } finally {

      setSavingHero(
        false
      );

    }

  };

  // =========================================
  // UPDATE CONTENT
  // =========================================

  const updateResultField = (
    index,
    field,
    value
  ) => {

    setResults(
      (prev) =>

        prev.map(
          (item, i) => {

            if (
              i !== index
            ) {

              return item;

            }

            return {

              ...item,

              [field]:
                value

            };

          }
        )

    );

  };

  // =========================================
  // CONTENT IMAGE
  // =========================================

  const handleResultImage = async (
    index,
    type,
    file
  ) => {

    if (!file) {

      return;

    }

    try {

      setUploadingResult({

        index,

        type

      });

      const imageUrl =
        await uploadImage(
          file
        );

      setResults(
        (prev) =>

          prev.map(
            (item, i) => {

              if (
                i !== index
              ) {

                return item;

              }

              return {

                ...item,

                [type]:
                  imageUrl

              };

            }
          )

      );

    } catch (err) {

      console.error(
        "CONTENT IMAGE ERROR:",
        err
      );

      alert(
        err.message ||
        "Error subiendo imagen."
      );

    } finally {

      setUploadingResult(
        null
      );

    }

  };

  // =========================================
  // ADD EXPERIENCE
  // =========================================

  const addResult = () => {

    setResults(
      (prev) => [

        ...prev,

        {

          before:
            "",

          after:
            "",

          title:
            "Nueva experiencia",

          description:
            "Describe una experiencia especial de los huéspedes en Orión Beach Hotel.",

          category:
            "Experiencia"

        }

      ]
    );

  };

  // =========================================
  // DELETE EXPERIENCE
  // =========================================

  const deleteResult = (
    index
  ) => {

    const confirmed =
      window.confirm(
        "¿Eliminar esta experiencia?"
      );

    if (!confirmed) {

      return;

    }

    setResults(
      (prev) =>
        prev.filter(
          (_, i) =>
            i !== index
        )
    );

  };

  // =========================================
  // SAVE CONTENT
  // =========================================

  const saveResults =
    async () => {

      try {

        setSavingResults(
          true
        );

        const response =
          await fetch(
            apiUrl(
              "/api/content/results"
            ),
            {

              method:
                "PUT",

              headers: {

                "Content-Type":
                  "application/json",

                Accept:
                  "application/json"

              },

              body:
                JSON.stringify({

                  content: {

                    items:
                      Array.isArray(
                        results
                      )
                        ? results
                        : []

                  }

                })

            }
          );

        const data =
          await readJson(
            response
          );

        if (!response.ok) {

          throw new Error(
            getApiError(
              data,
              response,
              `Error guardando contenido: HTTP ${response.status}`
            )
          );

        }

        alert(
          "✓ Contenido del hotel guardado correctamente."
        );

      } catch (err) {

        console.error(
          "SAVE CONTENT ERROR:",
          err
        );

        alert(

          `✕ ${
            err.message ||
            "Error guardando contenido."
          }`

        );

      } finally {

        setSavingResults(
          false
        );

      }

    };

  // =========================================
  // LOADING
  // =========================================

  if (loading) {

    return (

      <div className="cms-loading">

        <div className="cms-loading__brand">

          <span>
            ORION
          </span>

          <small>
            BEACH HOTEL
          </small>

        </div>

        <div className="cms-spinner"></div>

        <p>

          Preparando el panel
          {isProduction
            ? " de producción..."
            : " de desarrollo..."}

        </p>

      </div>

    );

  }

  // =========================================
  // RENDER
  // =========================================

  return (

    <div className="cms">

      {/* =====================================
          HEADER
      ===================================== */}

      <header className="cms__header">

        <div className="cms__header-brand">

          <div className="cms__eyebrow">
            ORION BEACH HOTEL
          </div>

          <h1>
            Panel de Experiencia
          </h1>

          <p>

            Gestiona el contenido digital de

            <strong>
              {" "}Orión Beach Hotel · Kite House
            </strong>

          </p>

        </div>

        <div className="cms__header-actions">

          <div className="cms__location">

            <span className="cms__location-dot"></span>

            La Guajira · Colombia

          </div>

          <div className="cms__local-status">

            <span></span>

            {isProduction
              ? "API PRODUCCIÓN"
              : "API DESARROLLO"}

          </div>

          <button
            type="button"
            onClick={loadAll}
            className="cms__refresh"
          >

            ↻

            <span>
              Actualizar
            </span>

          </button>

        </div>

      </header>


      {/* =====================================
          ERROR
      ===================================== */}

      {error && (

        <div className="cms__error">

          <div className="cms__error-icon">
            !
          </div>

          <div>

            <strong>
              No se pudo cargar completamente el CMS
            </strong>

            <p>
              {error}
            </p>

          </div>

          <button
            type="button"
            onClick={loadAll}
          >
            Reintentar
          </button>

        </div>

      )}


      {/* =====================================
          HERO
      ===================================== */}

      <section className="cms__card cms__card--hero">

        <div className="cms__section-header">

          <div className="cms__section-icon">
            ◇
          </div>

          <div className="cms__section-info">

            <span>
              EXPERIENCIA PRINCIPAL
            </span>

            <h2>
              Hero del hotel
            </h2>

            <p>
              La primera impresión que reciben
              tus huéspedes al entrar al sitio.
            </p>

          </div>

          <button
            type="button"
            className="cms__save"
            onClick={saveHero}
            disabled={
              savingHero
            }
          >

            <span>

              {savingHero
                ? "Guardando..."
                : "Guardar cambios"}

            </span>

          </button>

        </div>


        {/* ===================================
            HERO FORM
        =================================== */}

        <div className="cms__form-section">

          <div className="cms__section-label">

            <span>
              01
            </span>

            IDENTIDAD DEL MENSAJE

          </div>


          <div className="cms__grid">


            {/* BADGE */}

            <div className="cms__field">

              <label>
                Etiqueta
              </label>

              <input
                type="text"
                value={
                  hero?.content?.badge ||
                  ""
                }
                onChange={(e) =>
                  updateHeroField(
                    "badge",
                    e.target.value
                  )
                }
                placeholder="HOTEL ORIÓN • GUAJIRA"
              />

            </div>


            {/* TITLE */}

            <div className="cms__field">

              <label>
                Título principal
              </label>

              <input
                type="text"
                value={
                  hero?.content?.title ||
                  ""
                }
                onChange={(e) =>
                  updateHeroField(
                    "title",
                    e.target.value
                  )
                }
                placeholder="Descanso frente al paraíso"
              />

              <small>
                El mensaje principal del Hero.
              </small>

            </div>


            {/* SUBTITLE */}

            <div className="cms__field cms__field--large">

              <label>
                Subtítulo
              </label>

              <input
                type="text"
                value={
                  hero?.content?.subtitle ||
                  ""
                }
                onChange={(e) =>
                  updateHeroField(
                    "subtitle",
                    e.target.value
                  )
                }
                placeholder="Vive el viento. Descansa en Orión."
              />

            </div>


            {/* DESCRIPTION */}

            <div className="cms__field cms__field--full">

              <label>
                Descripción de la experiencia
              </label>

              <textarea
                rows="5"
                value={
                  hero?.content?.description ||
                  ""
                }
                onChange={(e) =>
                  updateHeroField(
                    "description",
                    e.target.value
                  )
                }
                placeholder="Describe la experiencia de hospedarse en Orión..."
              />

              <small>

                Utiliza un texto atractivo,
                natural y orientado a la experiencia.

              </small>

            </div>

          </div>

        </div>


        {/* ===================================
            RESERVATIONS
        =================================== */}

        <div className="cms__form-section">

          <div className="cms__section-label">

            <span>
              02
            </span>

            RESERVAS Y CONTACTO

          </div>


          <div className="cms__grid">


            {/* PHONE */}

            <div className="cms__field">

              <label>
                WhatsApp de reservas
              </label>

              <div className="cms__input-prefix">

                <span>
                  +57
                </span>

                <input
                  type="text"
                  value={
                    hero?.content?.phone ||
                    ""
                  }
                  onChange={(e) =>
                    updateHeroField(
                      "phone",
                      e.target.value
                    )
                  }
                  placeholder="3001234567"
                />

              </div>

            </div>


            {/* WHATSAPP TEXT */}

            <div className="cms__field">

              <label>
                Mensaje automático
              </label>

              <input
                type="text"
                value={
                  hero?.content?.whatsapp_text ||
                  ""
                }
                onChange={(e) =>
                  updateHeroField(
                    "whatsapp_text",
                    e.target.value
                  )
                }
                placeholder="Hola, quiero información sobre disponibilidad..."
              />

            </div>


            {/* PRIMARY BUTTON */}

            <div className="cms__field">

              <label>
                Botón principal
              </label>

              <input
                type="text"
                value={
                  hero?.content?.primary_button ||
                  ""
                }
                onChange={(e) =>
                  updateHeroField(
                    "primary_button",
                    e.target.value
                  )
                }
                placeholder="Reservar Ahora"
              />

            </div>


            {/* SECONDARY BUTTON */}

            <div className="cms__field">

              <label>
                Botón secundario
              </label>

              <input
                type="text"
                value={
                  hero?.content?.secondary_button ||
                  ""
                }
                onChange={(e) =>
                  updateHeroField(
                    "secondary_button",
                    e.target.value
                  )
                }
                placeholder="Ver Habitaciones"
              />

            </div>

          </div>

        </div>


        {/* ===================================
            HERO IMAGE
        =================================== */}

        <div className="cms__form-section">

          <div className="cms__section-label">

            <span>
              03
            </span>

            IMAGEN PRINCIPAL

          </div>


          <div className="cms__image-editor">

            <div className="cms__upload-area">

              <div className="cms__upload-icon">
                ↑
              </div>

              <div>

                <strong>
                  Imagen principal del hotel
                </strong>

                <p>
                  Recomendado: fotografía horizontal
                  de alta calidad.
                </p>

              </div>

              <label className="cms__upload-button">

                {uploadingHero
                  ? "Subiendo..."
                  : "Seleccionar imagen"}

                <input
                  type="file"
                  accept="image/*"
                  disabled={
                    uploadingHero
                  }
                  onChange={(e) =>
                    handleHeroImage(
                      e.target.files?.[0]
                    )
                  }
                />

              </label>

            </div>


            {hero?.images?.[0]?.url && (

              <div className="cms__hero-preview">

                <img
                  src={
                    hero.images[0].url
                  }
                  alt="Vista principal de Orión Beach Hotel"
                />

                <div className="cms__preview-overlay">

                  <span>
                    IMAGEN ACTUAL
                  </span>

                </div>

              </div>

            )}

          </div>

        </div>

      </section>


      {/* =====================================
          EXPERIENCES
      ===================================== */}

      <section className="cms__card">

        <div className="cms__section-header">

          <div className="cms__section-icon cms__section-icon--sand">
            ◇
          </div>

          <div className="cms__section-info">

            <span>
              CONTENIDO DEL HOTEL
            </span>

            <h2>
              Experiencias y galería
            </h2>

            <p>
              Agrega experiencias, fotografías,
              espacios y momentos especiales.
            </p>

          </div>


          <div className="cms__actions">

            <button
              type="button"
              className="cms__add"
              onClick={addResult}
            >

              <span>
                +
              </span>

              Agregar experiencia

            </button>

            <button
              type="button"
              className="cms__save"
              onClick={saveResults}
              disabled={
                savingResults
              }
            >

              {savingResults
                ? "Guardando..."
                : "Guardar contenido"}

            </button>

          </div>

        </div>


        {/* ===================================
            EMPTY
        =================================== */}

        {results.length === 0 && (

          <div className="cms__empty">

            <div className="cms__empty-icon">
              ◇
            </div>

            <span>
              GALERÍA VACÍA
            </span>

            <h3>
              Comienza a contar la historia de Orión
            </h3>

            <p>
              Agrega fotografías, experiencias,
              espacios del hotel o testimonios
              de tus huéspedes.
            </p>

            <button
              type="button"
              className="cms__add"
              onClick={addResult}
            >

              + Agregar primera experiencia

            </button>

          </div>

        )}


        {/* ===================================
            CONTENT LIST
        =================================== */}

        {results.length > 0 && (

          <div className="cms__results-list">

            {results.map(
              (
                item,
                index
              ) => (

                <article
                  key={
                    `experience-${index}`
                  }
                  className="cms__result"
                >

                  {/* HEADER */}

                  <div className="cms__result-top">

                    <div>

                      <span>

                        EXPERIENCIA
                        {" "}
                        {String(
                          index + 1
                        ).padStart(
                          2,
                          "0"
                        )}

                      </span>

                      <h3>

                        {item?.title ||
                          `Experiencia ${index + 1}`}

                      </h3>

                    </div>

                    <button
                      type="button"
                      className="cms__delete"
                      onClick={() =>
                        deleteResult(
                          index
                        )
                      }
                    >

                      Eliminar

                    </button>

                  </div>


                  {/* FIELDS */}

                  <div className="cms__grid">

                    {/* TITLE */}

                    <div className="cms__field">

                      <label>
                        Nombre
                      </label>

                      <input
                        type="text"
                        value={
                          item?.title ||
                          ""
                        }
                        onChange={(e) =>
                          updateResultField(
                            index,
                            "title",
                            e.target.value
                          )
                        }
                        placeholder="Habitación frente al mar"
                      />

                    </div>


                    {/* CATEGORY */}

                    <div className="cms__field">

                      <label>
                        Categoría
                      </label>

                      <select
                        value={
                          item?.category ||
                          "Experiencia"
                        }
                        onChange={(e) =>
                          updateResultField(
                            index,
                            "category",
                            e.target.value
                          )
                        }
                      >

                        <option>
                          Experiencia
                        </option>

                        <option>
                          Habitaciones
                        </option>

                        <option>
                          Kitesurf
                        </option>

                        <option>
                          Gastronomía
                        </option>

                        <option>
                          Naturaleza
                        </option>

                        <option>
                          Hotel
                        </option>

                        <option>
                          Playa
                        </option>

                        <option>
                          Testimonio
                        </option>

                      </select>

                    </div>


                    {/* DESCRIPTION */}

                    <div className="cms__field cms__field--full">

                      <label>
                        Descripción
                      </label>

                      <textarea
                        rows="4"
                        value={
                          item?.description ||
                          ""
                        }
                        onChange={(e) =>
                          updateResultField(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="Describe esta experiencia..."
                      />

                    </div>

                  </div>


                  {/* IMAGES */}

                  <div className="cms__result-images">


                    {/* IMAGE 1 */}

                    <div className="cms__image-box">

                      <div className="cms__image-box-header">

                        <div>

                          <span>
                            FOTOGRAFÍA 01
                          </span>

                          <strong>
                            Imagen principal
                          </strong>

                        </div>

                      </div>


                      <div className="cms__image-upload">

                        {item?.before ? (

                          <img
                            src={
                              item.before
                            }
                            alt={
                              item?.title ||
                              `Experiencia ${index + 1}`
                            }
                          />

                        ) : (

                          <div className="cms__image-placeholder">

                            <span>
                              +
                            </span>

                            <small>
                              Sin imagen
                            </small>

                          </div>

                        )}

                        <label>

                          {uploadingResult?.index === index &&
                          uploadingResult?.type === "before"

                            ? "Subiendo..."

                            : "Cambiar fotografía"}

                          <input
                            type="file"
                            accept="image/*"
                            disabled={
                              uploadingResult?.index === index &&
                              uploadingResult?.type === "before"
                            }
                            onChange={(e) =>
                              handleResultImage(
                                index,
                                "before",
                                e.target.files?.[0]
                              )
                            }
                          />

                        </label>

                      </div>

                    </div>


                    {/* IMAGE 2 */}

                    <div className="cms__image-box">

                      <div className="cms__image-box-header">

                        <div>

                          <span>
                            FOTOGRAFÍA 02
                          </span>

                          <strong>
                            Imagen adicional
                          </strong>

                        </div>

                      </div>


                      <div className="cms__image-upload">

                        {item?.after ? (

                          <img
                            src={
                              item.after
                            }
                            alt={
                              `${item?.title || "Experiencia"} adicional`
                            }
                          />

                        ) : (

                          <div className="cms__image-placeholder">

                            <span>
                              +
                            </span>

                            <small>
                              Sin imagen
                            </small>

                          </div>

                        )}

                        <label>

                          {uploadingResult?.index === index &&
                          uploadingResult?.type === "after"

                            ? "Subiendo..."

                            : "Agregar fotografía"}

                          <input
                            type="file"
                            accept="image/*"
                            disabled={
                              uploadingResult?.index === index &&
                              uploadingResult?.type === "after"
                            }
                            onChange={(e) =>
                              handleResultImage(
                                index,
                                "after",
                                e.target.files?.[0]
                              )
                            }
                          />

                        </label>

                      </div>

                    </div>

                  </div>

                </article>

              )
            )}

          </div>

        )}

      </section>


      {/* =====================================
          CMS FOOTER
      ===================================== */}

      <footer className="cms__footer">

        <div className="cms__footer-brand">

          <span>
            ORION
          </span>

          <small>
            BEACH HOTEL
          </small>

        </div>

        <div className="cms__footer-line"></div>

        <p>
          Kite House · La Guajira · Colombia
        </p>

        <small>
          Panel de administración ·{" "}
          {isProduction
            ? "API Producción"
            : "API Desarrollo"}
        </small>

      </footer>

    </div>

  );

}